import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDeleteTransaction, useTransactions } from "../../hooks/investments";
import type { TransactionDto } from "../../lib/investments";
import { TransactionFormModal } from "./TransactionFormModal";

function formatEur(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "—";
  return (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

interface Props {
  investmentId: string;
}

export function TransactionTable({ investmentId }: Props) {
  const { t } = useTranslation();
  const { data, isLoading } = useTransactions(investmentId, true);
  const deleteMutation = useDeleteTransaction(investmentId);
  const [editing, setEditing] = useState<TransactionDto | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  if (isLoading) return <p className="text-sm text-slate-500">...</p>;

  const transactions = data?.transactions ?? [];

  if (transactions.length === 0) {
    return (
      <p className="text-sm text-slate-500 py-4 text-center">
        {t("investments.transaction.no_transactions")}
      </p>
    );
  }

  async function handleDelete(id: string) {
    await deleteMutation.mutateAsync(id);
    setConfirmDeleteId(null);
  }

  function typeBadgeClass(type: TransactionDto["type"]): string {
    switch (type) {
      case "BUY":
        return "bg-green-100 text-green-700";
      case "SELL":
        return "bg-red-100 text-red-700";
      case "DIVIDEND":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-slate-100 text-slate-600";
    }
  }

  return (
    <>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="text-left border-b">
            <th className="p-2">{t("investments.transaction.header_date")}</th>
            <th className="p-2">{t("investments.transaction.header_type")}</th>
            <th className="p-2 text-right">{t("investments.transaction.header_shares")}</th>
            <th className="p-2 text-right">{t("investments.transaction.header_price")}</th>
            <th className="p-2 text-right">{t("investments.transaction.header_amount")}</th>
            <th className="p-2 text-right">{t("investments.transaction.header_fee")}</th>
            <th className="p-2 text-right">{t("investments.transaction.header_tax")}</th>
            <th className="p-2">{t("investments.transaction.header_source")}</th>
            <th scope="col" className="p-2">
              <span className="sr-only">{t("investments.transaction.header_actions")}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {transactions.map((tx) => (
            <tr key={tx.id} className="border-b">
              <td className="p-2 font-mono text-xs">
                {tx.date}
                {tx.time ? <span className="ml-1 text-slate-400">{tx.time}</span> : null}
              </td>
              <td className="p-2">
                <span className={`px-2 py-0.5 text-xs rounded ${typeBadgeClass(tx.type)}`}>
                  {t(`investments.transaction.type_${tx.type}`)}
                </span>
              </td>
              <td className="p-2 text-right font-mono text-xs">{tx.shares ?? "—"}</td>
              <td className="p-2 text-right font-mono">{formatEur(tx.priceCents)}</td>
              <td className="p-2 text-right font-mono">{formatEur(tx.amountCents)}</td>
              <td className="p-2 text-right font-mono text-xs">{formatEur(tx.feeCents)}</td>
              <td className="p-2 text-right font-mono text-xs">{formatEur(tx.taxCents)}</td>
              <td className="p-2">
                <span
                  className={`px-2 py-0.5 text-xs rounded ${
                    tx.source === "IMPORT"
                      ? "bg-amber-100 text-amber-700"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {t(`investments.transaction.source_${tx.source}`)}
                </span>
              </td>
              <td className="p-2 text-right whitespace-nowrap">
                <button
                  type="button"
                  className="text-xs text-blue-600 underline mr-2"
                  onClick={() => setEditing(tx)}
                >
                  {t("investments.transaction.edit_button")}
                </button>
                {confirmDeleteId === tx.id ? (
                  <>
                    <button
                      type="button"
                      className="text-xs text-red-700 underline mr-1"
                      onClick={() => handleDelete(tx.id)}
                      disabled={deleteMutation.isPending}
                    >
                      {t("investments.transaction.delete_confirm")}
                    </button>
                    <button
                      type="button"
                      className="text-xs text-slate-500 underline"
                      onClick={() => setConfirmDeleteId(null)}
                    >
                      {t("common.cancel")}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="text-xs text-red-700"
                    onClick={() => setConfirmDeleteId(tx.id)}
                  >
                    {t("investments.transaction.delete_button")}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {editing && (
        <TransactionFormModal
          investmentId={investmentId}
          existing={editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}
